import { useState, useEffect } from 'react';
import Hero from './components/Hero';
import Form from './components/Form';
import Analysis from './components/Analysis';
import HowItWorks from './components/HowItWorks';
import Education from './components/Education';
import Calculator from './components/Calculator';
import FAQ from './components/FAQ';
import Governance from './components/Governance';
import Footer from './components/Footer';

export default function HomePage() {
  const [language, setLanguage] = useState<'pt' | 'en'>('pt');
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrollProgress, setScrollProgress] = useState(0);
  const [formOpen, setFormOpen] = useState(false);

  useEffect(() => {
    if (menuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [menuOpen]);

  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY;
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      const progress = docHeight > 0 ? (scrollTop / docHeight) * 100 : 0;
      setScrollProgress(progress);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = {
    pt: [
      { label: 'Como Funciona', href: '#how-it-works' },
      { label: 'Educação', href: '#education' },
      { label: 'Simulador', href: '#calculator' },
      { label: 'FAQ', href: '#faq' },
      { label: 'Governança', href: '#governance' },
    ],
    en: [
      { label: 'How It Works', href: '#how-it-works' },
      { label: 'Education', href: '#education' },
      { label: 'Simulator', href: '#calculator' },
      { label: 'FAQ', href: '#faq' },
      { label: 'Governance', href: '#governance' },
    ],
  };

  return (
    <div className="min-h-screen bg-[#F6F2EC]">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 bg-black backdrop-blur-sm border-b border-white/10 z-50">
        {/* Scroll Progress Bar */}
        <div className="absolute bottom-0 left-0 right-0 h-[2px] bg-white/10">
          <div
            className="h-full bg-[#F97700] transition-all duration-100 ease-out"
            style={{ width: `${scrollProgress}%` }}
          />
        </div>

        <div className="max-w-7xl mx-auto px-4 lg:px-8 py-4 flex items-center justify-between">
          {/* Logo */}
          <a href="#" className="flex items-center gap-3 cursor-pointer group">
            <img
              src="https://static.readdy.ai/image/eead7521a07a7e44430d3022e12ebfe4/7fea8a9af7d757ad59f0fb7a71738a19.png"
              alt="FGT Capital"
              className="h-10 w-auto object-contain self-center"
            />
            <div className="w-px h-7 bg-white/25 self-center" />
            <span className="hidden sm:block text-[14px] font-bold uppercase tracking-[0.13em] leading-none text-white group-hover:text-[#F97700] transition-colors duration-200 self-center">
              {language === 'pt' ? 'Engenharia Patrimonial' : 'Wealth Engineering'}
            </span>
          </a>

          {/* Desktop nav + actions */}
          <div className="hidden md:flex items-center gap-6">
            <div className="flex items-center gap-2 bg-white/10 px-1 py-1 rounded-full">
              <button
                onClick={() => setLanguage('pt')}
                className={`px-4 py-2 text-sm font-semibold transition-all whitespace-nowrap cursor-pointer rounded-full ${
                  language === 'pt' ? 'bg-[#F97700] text-white' : 'text-white/80 hover:text-[#F97700]'
                }`}
              >
                PT
              </button>
              <button
                onClick={() => setLanguage('en')}
                className={`px-4 py-2 text-sm font-semibold transition-all whitespace-nowrap cursor-pointer rounded-full ${
                  language === 'en' ? 'bg-[#F97700] text-white' : 'text-white/80 hover:text-[#F97700]'
                }`}
              >
                EN
              </button>
            </div>
            <button
              onClick={() => setFormOpen(true)}
              className="inline-flex items-center justify-center px-6 py-3 bg-[#F97700] text-white text-sm font-semibold tracking-wide hover:bg-[#E56E00] transition-colors whitespace-nowrap cursor-pointer"
            >
              {language === 'pt' ? 'Agendar Diagnóstico' : 'Schedule Assessment'}
            </button>
          </div>

          {/* Mobile: language toggle + hamburger */}
          <div className="flex md:hidden items-center gap-3">
            <div className="flex items-center gap-1 bg-white/10 px-1 py-1 rounded-full">
              <button
                onClick={() => setLanguage('pt')}
                className={`px-3 py-1.5 text-xs font-semibold transition-all whitespace-nowrap cursor-pointer rounded-full ${
                  language === 'pt' ? 'bg-[#F97700] text-white' : 'text-white/80'
                }`}
              >
                PT
              </button>
              <button
                onClick={() => setLanguage('en')}
                className={`px-3 py-1.5 text-xs font-semibold transition-all whitespace-nowrap cursor-pointer rounded-full ${
                  language === 'en' ? 'bg-[#F97700] text-white' : 'text-white/80'
                }`}
              >
                EN
              </button>
            </div>
            <button
              onClick={() => setMenuOpen(!menuOpen)}
              className="w-10 h-10 flex flex-col items-center justify-center gap-1.5 cursor-pointer"
              aria-label="Menu"
            >
              <span
                className={`block w-6 h-0.5 bg-white transition-all duration-300 origin-center ${
                  menuOpen ? 'rotate-45 translate-y-2' : ''
                }`}
              />
              <span
                className={`block w-6 h-0.5 bg-white transition-all duration-300 ${
                  menuOpen ? 'opacity-0 scale-x-0' : ''
                }`}
              />
              <span
                className={`block w-6 h-0.5 bg-white transition-all duration-300 origin-center ${
                  menuOpen ? '-rotate-45 -translate-y-2' : ''
                }`}
              />
            </button>
          </div>
        </div>

        {/* Mobile Menu Drawer */}
        <div
          className={`md:hidden overflow-hidden transition-all duration-300 ease-in-out ${
            menuOpen ? 'max-h-screen opacity-100' : 'max-h-0 opacity-0'
          }`}
        >
          <div className="bg-black border-t border-white/10 px-6 py-6 flex flex-col gap-1">
            {navLinks[language].map((link) => (
              <a
                key={link.href}
                href={link.href}
                onClick={() => setMenuOpen(false)}
                className="py-3 text-base font-medium text-white/80 border-b border-white/10 last:border-0 hover:text-[#F97700] transition-colors cursor-pointer"
              >
                {link.label}
              </a>
            ))}
            <div className="pt-4 flex flex-col gap-3">
              <button
                onClick={() => {
                  setFormOpen(true);
                  setMenuOpen(false);
                }}
                className="inline-flex items-center justify-center px-6 py-3.5 bg-[#F97700] text-white text-sm font-semibold tracking-wide hover:bg-[#E56E00] transition-colors whitespace-nowrap cursor-pointer"
              >
                {language === 'pt' ? 'Agendar Diagnóstico' : 'Schedule Assessment'}
              </button>
              <a
                href="https://wa.me/5511930322408"
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => setMenuOpen(false)}
                className="inline-flex items-center justify-center gap-2 px-6 py-3.5 border border-white/20 text-white text-sm font-semibold hover:border-[#F97700] hover:text-[#F97700] transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-whatsapp-line text-base"></i>
                {language === 'pt' ? 'Falar no WhatsApp' : 'Chat on WhatsApp'}
              </a>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="pt-20">
        <Hero language={language} onOpenForm={() => setFormOpen(true)} />
        <Analysis language={language} />
        <HowItWorks language={language} onOpenForm={() => setFormOpen(true)} />
        <Education language={language} />
        <Calculator language={language} />
        <FAQ language={language} />
        <Governance language={language} />
        <Footer language={language} />
      </main>

      {/* Form Modal */}
      <Form language={language} isOpen={formOpen} onClose={() => setFormOpen(false)} />

      {/* Floating WhatsApp Button */}
      <a
        href="https://wa.me/5511930322408"
        target="_blank"
        rel="noopener noreferrer"
        aria-label="WhatsApp"
        className="fixed bottom-6 right-6 z-50 group flex items-center gap-0 hover:gap-3 overflow-hidden transition-all duration-300 ease-in-out cursor-pointer"
      >
        <span className="max-w-0 group-hover:max-w-xs opacity-0 group-hover:opacity-100 transition-all duration-300 ease-in-out whitespace-nowrap text-sm font-semibold text-white bg-[#25D366] px-4 py-3.5 rounded-l-full">
          {language === 'pt' ? 'Falar com Especialista' : 'Speak with Specialist'}
        </span>
        <div className="w-14 h-14 rounded-full bg-[#25D366] flex items-center justify-center shadow-lg group-hover:shadow-xl transition-shadow duration-300 flex-shrink-0">
          <i className="ri-whatsapp-line text-white text-2xl"></i>
        </div>
      </a>
    </div>
  );
}